// OperationReport43Component.jsx
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Label, Table } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import DatePicker, { registerLocale } from "react-datepicker";
import { format, parseISO } from "date-fns";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import { GetVMdDept } from "../../share/api/MasterDataApi";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

const thaiMonths = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const years = range(1963, new Date().getFullYear() + 8);

// ===== helpers =====
const groupBy = (arr, key) =>
  arr.reduce((a, it) => {
    const k = (it[key] ?? "").toString();
    (a[k] ??= []).push(it);
    return a;
  }, {});
const isUniform = (rows, field) =>
  rows.every(r => (r[field] ?? "") === (rows[0]?.[field] ?? ""));

const N = v => Number(v ?? 0);
function fmtMoney(n) { const num = Number(String(n ?? 0).replace(/,/g, "")); return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }); }
function toThaiDateLabel(v) {
  if (!v) return "-";
  if (typeof v === "string" && /\s[ก-๙]/.test(v)) return v;
  const d = typeof v === "string" ? parseISO(v) : v;
  const be = addYears(543, d); const monthName = format(be, "MMMM", { locale: th });
  return `${format(be, "dd")} ${monthName} ${format(be, "yyyy")}`;
}
function formatMonthYearBE(date) { if (!date) return ""; const be = addYears(543, date); return format(be, "MMMM yyyy", { locale: th }); }
function pick(...c) { for (const x of c) { if (x !== undefined && x !== null && String(x).trim() !== "") return x; } return ""; }
function splitBranchLevel(name) {
  const s = String(name || "").trim();
  if (!s) return { branch: "-", level: "-" };
  const m = s.match(/ระดับ(?:ที่)?\s*([0-9]+)/i);
  const level = m ? m[1] : "-";
  const branch = s.replace(/\s*ระดับ(?:ที่)?\s*[0-9]+.*/i, "").trim() || s;
  if (branch === "ระดับ1") return { branch: "-", level: level };
  return { branch, level };
}

// ===== PDF Thai Font =====
function base64FromArrayBuffer(buf) { const b = new Uint8Array(buf); const c = 0x8000; let s = ""; for (let i = 0; i < b.length; i += c) { s += String.fromCharCode.apply(null, b.subarray(i, i + c)); } return btoa(s); }
let thaiFontLoaded = false;
async function ensureThaiFontLoaded() {
  if (thaiFontLoaded) return;
  const [regRes, boldRes] = await Promise.all([fetch("/fonts/Sarabun-Regular.ttf"), fetch("/fonts/Sarabun-Bold.ttf")]);
  const [regBuf, boldBuf] = await Promise.all([regRes.arrayBuffer(), boldRes.arrayBuffer()]);
  const regB64 = base64FromArrayBuffer(regBuf); const boldB64 = base64FromArrayBuffer(boldBuf);
  jsPDF.API.events.push(['addFonts', function () {
    this.addFileToVFS("Sarabun-Regular.ttf", regB64);
    this.addFileToVFS("Sarabun-Bold.ttf", boldB64);
    this.addFont("Sarabun-Regular.ttf", "Sarabun", "normal");
    this.addFont("Sarabun-Bold.ttf", "Sarabun", "bold");
  }]);
  new jsPDF(); thaiFontLoaded = true;
}

export function OperationReport43Component() {

  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonth = String(date.getMonth() + 1).padStart(2, "0");

  const [reportMonth, setReportMonth] = useState("");
  const [reportMonthDate, setReportMonthDate] = useState(null);

  const [reportMonthEnd, setReportMonthEnd] = useState(`${currentYear}-${currentMonth}`); // "YYYY-MM"
  const [reportMonthEndDate, setReportMonthEndDate] = useState(new Date(currentYear, Number(currentMonth) - 1, 1));
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const roleId = String(user?.roleId ?? user?.roldId ?? "");
  const userDeptId = String(user?.deptId ?? "all");

  const [deptsData, setDeptsData] = useState([]);
  const [deptOptions, setDeptOptions] = useState([]);
  const [deptIdFilter, setDeptIdFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  // เงื่อนไขแสดงคอลัมน์หน่วยงาน
  const showDeptCol =
    String(user?.roldId ?? roleId) === "4" &&
    String(deptIdFilter || "all") === "all";

  useEffect(() => { GetVMdDept({ setDeptsData, setLoading }); }, []);
  useEffect(() => {
    const mapped = (deptsData || []).map(d => ({ value: String(d?.value ?? d?.deptId ?? d?.DeptId ?? d?.id ?? ""), label: String(d?.label ?? d?.deptName ?? d?.DeptName ?? d?.name ?? "") }));
    if (roleId === "4") { setDeptOptions([{ value: "all", label: "ทุกหน่วยงาน" }, ...mapped]); setDeptIdFilter(p => p ?? "all"); }
    else if (roleId === "3") { const self = mapped.find(o => o.value === userDeptId) || { value: userDeptId, label: "หน่วยงานของฉัน" }; setDeptOptions([self]); setDeptIdFilter(userDeptId); }
    else { setDeptOptions(mapped); setDeptIdFilter(p => p || (mapped.find(o => o.value === userDeptId)?.value ?? "")); }
  }, [deptsData, roleId, userDeptId]);


  useEffect(() => {
    if (!reportMonth) { setReportMonthDate(null); return; }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  useEffect(() => {
    if (!reportMonthEnd) { setReportMonthEndDate(null); return; }
    const [y, m] = reportMonthEnd.split("-").map(Number);
    setReportMonthEndDate(new Date(y, m - 1, 1));
  }, [reportMonthEnd]);

  // โหลดข้อมูล
  const checkAndLoadMonth = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("monthYear", reportMonth === "" ? "all" : reportMonth);
      fd.append("monthYearEnd", reportMonthEnd === "" ? "all" : reportMonthEnd);
      fd.append("deptIdFilter", deptIdFilter || "all");

      const res = await fetch("/api/SupportOperation/listSupportOperation43", {
        method: "POST",
        headers: { token: user.token },
        body: fd
      });
      const j = await res.json();
      const raw = Array.isArray(j?.dataenum) ? j.dataenum : [];

      const mapped = raw.map(x => {
        const SupportNo = pick(x.SupportNo, x.supportNo);
        const SupportName = pick(x.SupportName, x.supportName);
        const DeptName = pick(x.DeptName, x.deptName); // <<< เพิ่ม DeptName
        const certNo = pick(x.SkillStandardCertNoRmth3, x.skillStandardCertNoRmth3);
        const certDate = pick(x.CertificateDate, x.certificateDate);
        const lawOcc = pick(x.TestOccupationName, x.testOccupationName);
        const { branch, level } = splitBranchLevel(lawOcc);
        const empTotal = pick(x.EmployeeTotal, x.employeeTotal);
        const personTotal = pick(x.PersonTotal, x.personTotal);
        const payAmt = pick(x.PaymentAmount, x.paymentAmount);
        const payDate = pick(x.PaymentDate, x.paymentDate);
        const lawOccupationName = pick(x.LawOccupationName, x.lawOccupationName);
        return {
          SupportNo,
          SupportName,
          DeptName,
          CertNo: certNo || "-",
          CertDate: certDate || "",
          Branch: branch,
          Level: level,
          EmployeeTotal: N(empTotal),
          PersonTotal: N(personTotal),
          PaymentAmount: N(String(payAmt ?? 0).replace(/,/g, "")),
          PaymentDate: payDate || "",
          LawOccupationName: lawOccupationName
        };
      });

      setItems(mapped);
    } catch (e) {
      Swal.fire("โหลดข้อมูลไม่สำเร็จ", "", "error");
      setItems([]);
    } finally { setLoading(false); }
  };

  // รวมยอดเงิน
  // const totalPayment = items.reduce((s, r) => s + N(r.PaymentAmount), 0);
  const totalPayment = Object?.values(groupBy(items, "SupportNo"))?.reduce((sum, rows) => {
    return sum + N(rows[0]?.PaymentAmount);
  }, 0);

  // ===== Header 3 แถว (dynamic DeptName) =====
  const head1 = [
    "ที่",
    "เลขที่คำขอ",
    "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน",
    ...(showDeptCol ? [{ content: "หน่วยงานที่อนุมัติคำขอ", rowSpan: 3 }] : []),
    { content: "การรับรองมาตรฐานฝีมือแรงงานของผู้ประกอบอาชีพ", colSpan: 4 },
    { content: "ดำเนินการทดสอบให้แก่ลูกจ้าง", colSpan: 2 },
    { content: "จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)", rowSpan: 3 },
    { content: "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน", rowSpan: 3 },
  ];
  const head2 = [
    "", "", ...(showDeptCol ? [""] : []),
    { content: "หนังสือรับรองเลขที่", rowSpan: 2 },
    { content: "ออกให้ ณ วันที่", rowSpan: 2 },
    { content: "สาขาอาชีพ", colSpan: 2 },
    { content: "ลูกจ้างในตำแหน่งงานที่เกี่ยวข้อง (คน)", rowSpan: 2 },
    { content: "จำนวนผู้เข้ารับการทดสอบ (คน)", rowSpan: 2 },
    "", "" // money/date rowSpan in head1
  ];
  const head3 = [
    "", "", ...(showDeptCol ? [""] : []),
    "", "", "สาขา", "ระดับ", "", "", "", ""
  ];

  // ดัชนีคอลัมน์ (leaf) แบบ dynamic
  const COL = showDeptCol
    ? {
      No: 0,
      SupportNo: 1,
      SupportName: 2,
      DeptName: 3,

      CertNo: 4,
      CertDate: 5,
      LawOcc: 6,      // ✅ เพิ่ม
      Branch: 7,
      Level: 8,

      Emp: 9,
      Person: 10,
      Money: 11,
      PayDate: 12,
    }
    : {
      No: 0,
      SupportNo: 1,
      SupportName: 2,

      CertNo: 3,
      CertDate: 4,
      LawOcc: 5,      // ✅ เพิ่ม
      Branch: 6,
      Level: 7,

      Emp: 8,
      Person: 9,
      Money: 10,
      PayDate: 11,
    };


  // ===== Build AoA & merges (dynamic DeptName) =====
  const buildAoaAndMerges = () => {

    // ===== HEADER =====
    const row1 = [
      "ที่",
      "เลขที่คำขอ",
      "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน",
      ...(showDeptCol ? ["หน่วยงานที่อนุมัติคำขอ"] : []),

      "การรับรองมาตรฐานฝีมือแรงงานของผู้ประกอบอาชีพ", "", "", "", "",
      "ดำเนินการทดสอบให้แก่ลูกจ้าง", "",
      "จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)",
      "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน",
    ];

    const row2 = [
      "", "", "",
      ...(showDeptCol ? [""] : []),

      "หนังสือรับรองเลขที่",
      "ออกให้ ณ วันที่",
      "สาขาอาชีพ",
      "สาขา",
      "ระดับ",
      "ลูกจ้างในตำแหน่งงานที่เกี่ยวข้อง (คน)",
      "จำนวนผู้เข้ารับการทดสอบ (คน)",
      "", ""
    ];

    const row3 = [
      "", "", "",
      ...(showDeptCol ? [""] : []),

      "", "", "", "", "",
      "", "",
      "", ""
    ];

    const aoa = [row1, row2, row3];
    const merges = [];

    // ===== FIXED COLUMNS (rowSpan 3) =====
    const fixed = showDeptCol ? 4 : 3;
    for (let c = 0; c < fixed; c++) {
      merges.push({ s: { r: 0, c }, e: { r: 2, c } });
    }

    // ===== CERT GROUP (5 columns) =====
    const certStart = fixed;
    merges.push({ s: { r: 0, c: certStart }, e: { r: 0, c: certStart + 4 } });

    // rowSpan 2 (แถว 2–3)
    merges.push({ s: { r: 1, c: certStart + 0 }, e: { r: 2, c: certStart + 0 } }); // CertNo
    merges.push({ s: { r: 1, c: certStart + 1 }, e: { r: 2, c: certStart + 1 } }); // CertDate
    merges.push({ s: { r: 1, c: certStart + 2 }, e: { r: 2, c: certStart + 2 } }); // LawOcc
    merges.push({ s: { r: 1, c: certStart + 3 }, e: { r: 2, c: certStart + 3 } }); // Branch
    merges.push({ s: { r: 1, c: certStart + 4 }, e: { r: 2, c: certStart + 4 } }); // Level

    // ===== TEST GROUP (2 columns) =====
    const testStart = certStart + 5;
    merges.push({ s: { r: 0, c: testStart }, e: { r: 0, c: testStart + 1 } });
    merges.push({ s: { r: 1, c: testStart }, e: { r: 2, c: testStart } });
    merges.push({ s: { r: 1, c: testStart + 1 }, e: { r: 2, c: testStart + 1 } });

    // ===== MONEY / DATE (rowSpan 3) =====
    const moneyCol = testStart + 2;
    const payDateCol = testStart + 3;
    merges.push({ s: { r: 0, c: moneyCol }, e: { r: 2, c: moneyCol } });
    merges.push({ s: { r: 0, c: payDateCol }, e: { r: 2, c: payDateCol } });

    // ===== DATA ROWS =====
    let cur = 3;
    Object.entries(groupBy(items, "SupportNo")).forEach(([_, rows], gIdx) => {
      const span = rows.length;
      const uPayAmt = isUniform(rows, "PaymentAmount");
      const uPayDt = isUniform(rows, "PaymentDate");

      rows.forEach((r, idx) => {
        const row = [];

        row[COL.No] = idx === 0 ? gIdx + 1 : "";
        row[COL.SupportNo] = idx === 0 ? r.SupportNo || "-" : "";
        row[COL.SupportName] = idx === 0 ? r.SupportName || "-" : "";
        if (showDeptCol) row[COL.DeptName] = idx === 0 ? r.DeptName || "-" : "";

        row[COL.CertNo] = r.CertNo || "-";
        row[COL.CertDate] = toThaiDateLabel(r.CertDate);
        row[COL.LawOcc] = r.LawOccupationName || "-";
        row[COL.Branch] = r.Branch || "-";
        row[COL.Level] = r.Level || "-";

        row[COL.Emp] = r.EmployeeTotal;
        row[COL.Person] = r.PersonTotal;

        row[COL.Money] = uPayAmt ? (idx === 0 ? r.PaymentAmount : "") : r.PaymentAmount;
        row[COL.PayDate] = uPayDt ? (idx === 0 ? toThaiDateLabel(r.PaymentDate) : "") : toThaiDateLabel(r.PaymentDate);

        for (let c = 0; c <= COL.PayDate; c++) {
          if (row[c] === undefined) row[c] = "";
        }

        aoa.push(row);
        cur++;
      });

      merges.push({ s: { r: cur - span, c: COL.SupportNo }, e: { r: cur - 1, c: COL.SupportNo } });
      merges.push({ s: { r: cur - span, c: COL.SupportName }, e: { r: cur - 1, c: COL.SupportName } });
      if (showDeptCol) merges.push({ s: { r: cur - span, c: COL.DeptName }, e: { r: cur - 1, c: COL.DeptName } });
      if (uPayAmt) merges.push({ s: { r: cur - span, c: COL.Money }, e: { r: cur - 1, c: COL.Money } });
      if (uPayDt) merges.push({ s: { r: cur - span, c: COL.PayDate }, e: { r: cur - 1, c: COL.PayDate } });
    });

    return { aoa, merges };
  };


  const handleExportExcel = () => {
    if (!items.length) return Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");

    const { aoa, merges } = buildAoaAndMerges();
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws["!merges"] = merges;
    ws["!cols"] = showDeptCol
      ? [
        { wch: 4 }, { wch: 18 }, { wch: 42 }, { wch: 28 },
        { wch: 22 }, { wch: 18 }, { wch: 30 }, { wch: 20 }, { wch: 10 },
        { wch: 20 }, { wch: 22 },
        { wch: 22 }, { wch: 22 },
      ]
      : [
        { wch: 4 }, { wch: 18 }, { wch: 42 },
        { wch: 22 }, { wch: 18 }, { wch: 30 }, { wch: 20 }, { wch: 10 },
        { wch: 20 }, { wch: 22 },
        { wch: 22 }, { wch: 22 },
      ];

    const footerRow = aoa.length;
    ws[XLSX.utils.encode_cell({ r: footerRow, c: 0 })] = { t: "s", v: "รวมจำนวนเงินช่วยเหลือ" };
    ws[XLSX.utils.encode_cell({ r: footerRow, c: COL.Money })] = { t: "n", v: totalPayment };
    ws["!merges"].push({ s: { r: footerRow, c: 0 }, e: { r: footerRow, c: COL.Money - 1 } });

    XLSX.utils.book_append_sheet(wb, ws, "รายงาน ชอ.4-3");
    XLSX.writeFile(wb, "รายงาน ชอ4-3.xlsx");
  };


  const handleExportPDF = async () => {
    if (!items.length) {
      Swal.fire("ไม่มีข้อมูลสำหรับส่งออก", "", "warning");
      return;
    }

    await ensureThaiFontLoaded();

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a3",
    });

    // ===== Title =====
    doc.setFont("Sarabun", "bold");
    doc.setFontSize(16);
    doc.text(
      "แบบรายงานผลการพิจารณาอนุมัติการให้เงินช่วยเหลือหรืออุดหนุนจากกองทุนพัฒนาฝีมือแรงงาน ชอ.4-3",
      40,
      40
    );

    // ===== HEADER (ตรงกับ Table บนจอ) =====
    const head = [[
      "ที่",
      "เลขที่คำขอ",
      "ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน",
      ...(showDeptCol ? ["หน่วยงานที่อนุมัติคำขอ"] : []),
      "หนังสือรับรองเลขที่",
      "ออกให้ ณ วันที่",
      "สาขาอาชีพ",
      "สาขา",
      "ระดับ",
      "ลูกจ้างในตำแหน่งงานที่เกี่ยวข้อง (คน)",
      "จำนวนผู้เข้ารับการทดสอบ (คน)",
      "จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)",
      "วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน",
    ]];

    // ===== BODY =====
    const body = [];

    Object.entries(groupBy(items, "SupportNo")).forEach(([_, rows], gIdx) => {
      const span = rows.length;
      const uPayAmt = isUniform(rows, "PaymentAmount");
      const uPayDt = isUniform(rows, "PaymentDate");

      rows.forEach((r, idx) => {
        body.push([
          idx === 0 ? gIdx + 1 : "",
          idx === 0 ? r.SupportNo || "-" : "",
          idx === 0 ? r.SupportName || "-" : "",
          ...(showDeptCol ? [idx === 0 ? r.DeptName || "-" : ""] : []),
          r.CertNo || "-",
          toThaiDateLabel(r.CertDate),
          r.LawOccupationName || "-",
          r.Branch || "-",
          r.Level || "-",
          r.EmployeeTotal,
          r.PersonTotal,
          uPayAmt ? (idx === 0 ? fmtMoney(rows[0].PaymentAmount) : "") : fmtMoney(r.PaymentAmount),
          uPayDt ? (idx === 0 ? toThaiDateLabel(rows[0].PaymentDate) : "") : toThaiDateLabel(r.PaymentDate),
        ]);
      });
    });

    // ===== TABLE =====
    doc.setFont("Sarabun", "normal");
    doc.autoTable({
      head,
      body,
      startY: 60,
      styles: {
        font: "Sarabun",
        fontSize: 10,
        cellPadding: 4,
        valign: "middle",
        overflow: "linebreak",
      },
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: 0,
        fontStyle: "bold",
        halign: "center",
      },
      columnStyles: {
        2: { halign: "left" }, // ชื่อผู้ขอ

        ...(showDeptCol ? { 3: { halign: "left" } } : {}),

        4: { cellWidth: 70, halign: "left" },   // หนังสือรับรองเลขที่
        5: { cellWidth: 90, halign: "center" }, // ออกให้ ณ วันที่ (ขยายให้ใหญ่ขึ้น)

        6: { cellWidth: 90, halign: "left" },   // ✅ สาขาอาชีพ (บีบลง)
        7: { cellWidth: 80, halign: "left" },   // ✅ สาขา (บีบลง)
        8: { cellWidth: 40, halign: "center" }, // ระดับ

        9: { cellWidth: 70, halign: "right" },  // ลูกจ้าง
        10: { cellWidth: 70, halign: "right" },  // ผู้เข้าทดสอบ

        11: { cellWidth: 90, halign: "right" },  // จำนวนเงิน
        12: { cellWidth: 80, halign: "center" }, // วันที่จ่าย
      },

      didDrawPage: data => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(9);
        doc.text(
          `หน้า ${data.pageNumber} / ${pageCount}`,
          data.settings.margin.left,
          doc.internal.pageSize.getHeight() - 10
        );
      },
    });

    // ===== FOOTER SUM =====
    doc.setFont("Sarabun", "bold");
    doc.setFontSize(12);
    doc.text(
      `รวมจำนวนเงินช่วยเหลือ: ${fmtMoney(totalPayment)} บาท`,
      40,
      doc.lastAutoTable.finalY + 20
    );

    doc.save("รายงาน ชอ4-3.pdf");
  };





  return (
    <Container className="py-3">
      <Row className="g-3 align-items-end">
        <Col md={6}>
          <Label>หน่วยงานกรมพัฒนาฝีมือแรงงาน</Label>
          <Select
            options={deptOptions}
            value={deptOptions.find(o => o.value === (deptIdFilter || "all")) || null}
            onChange={opt => setDeptIdFilter(opt?.value ?? "all")}
            isDisabled={roleId === "3"}
            className="text-center input-color"
            styles={{ control: (b) => ({ ...b, borderRadius: "20px" }), singleValue: (b) => ({ ...b, color: "#0A66C2" }) }}
            placeholder="เลือกหน่วยงาน"
            noOptionsMessage={() => "ไม่พบหน่วยงาน"}
          />
        </Col>
        <Col md={3}>
          <Label>รอบรายงาน (เดือน/ปี) (เริ่มต้น)</Label>
          <DatePicker
            selected={reportMonthDate}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                const y = date.getFullYear(); const m = String(date.getMonth() + 1).padStart(2, "0");
                setReportMonth(`${y}-${m}`); setReportMonthDate(new Date(y, Number(m) - 1, 1));
              } else { setReportMonth(""); setReportMonthDate(null); }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker showFullMonthYearPicker
            locale="th-buddhist"
            placeholderText="เลือกเดือน/ปี (พ.ศ.)"
            value={formatMonthYearBE(reportMonthDate)}
            renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
              <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <select className="form-select form-select-sm" style={{ width: 120 }} value={thaiMonths[getMonth(date)]} onChange={({ target: { value } }) => changeMonth(thaiMonths.indexOf(value))}>
                    {thaiMonths.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 100 }} value={getYear(date)} onChange={({ target: { value } }) => changeYear(Number(value))}>
                    {years.map(y => <option key={y} value={y}>{y + 543}</option>)}
                  </select>
                </div>
                <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
              </div>
            )}
            customInput={<input className="form-control rounded-pill text-center input-color" readOnly value={formatMonthYearBE(reportMonthDate)} placeholder="เลือกเดือน/ปี (พ.ศ.)" />}
          />
        </Col>

        <Col md={3}>
          <Label>รอบรายงาน (เดือน/ปี) สิ้นสุด</Label>
          <DatePicker
            selected={reportMonthEndDate}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, "0");
                setReportMonthEnd(`${y}-${m}`);
                setReportMonthEndDate(new Date(y, Number(m) - 1, 1));
              } else {
                setReportMonthEnd("");
                setReportMonthEndDate(null);
              }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            showFullMonthYearPicker
            locale="th-buddhist"
            placeholderText="เลือกเดือน/ปี (พ.ศ.)"
            value={formatMonthYearBE(reportMonthEndDate)}
            renderCustomHeader={({
              date, changeYear, changeMonth, decreaseMonth, increaseMonth,
              prevMonthButtonDisabled, nextMonthButtonDisabled,
            }) => (
              <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                <button type="button" className="btn btn-sm btn-light"
                  onClick={decreaseMonth} disabled={prevMonthButtonDisabled} title="เดือนก่อนหน้า">‹</button>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <select
                    className="form-select form-select-sm" style={{ width: 120 }}
                    value={thaiMonths[getMonth(date)]}
                    onChange={({ target: { value } }) => changeMonth(thaiMonths.indexOf(value))}
                  >
                    {thaiMonths.map((m) => (<option key={m} value={m}>{m}</option>))}
                  </select>
                  <select
                    className="form-select form-select-sm" style={{ width: 100 }}
                    value={getYear(date)}
                    onChange={({ target: { value } }) => changeYear(Number(value))}
                  >
                    {years.map((y) => (<option key={y} value={y}>{y + 543}</option>))}
                  </select>
                </div>
                <button type="button" className="btn btn-sm btn-light"
                  onClick={increaseMonth} disabled={nextMonthButtonDisabled} title="เดือนถัดไป">›</button>
              </div>
            )}
            customInput={
              <input
                className="form-control rounded-pill text-center input-color"
                readOnly
                value={formatMonthYearBE(reportMonthEndDate)}
                placeholder="เลือกเดือน/ปี (พ.ศ.)"
              />
            }
          />
        </Col>

        <Col md="auto" className="pt-4 d-flex justify-content-end gap-2">
          <button className="btn btn-primary" onClick={checkAndLoadMonth}>ตรวจสอบ/โหลดรายงาน</button>
          <button className="btn btn-outline-success" onClick={handleExportExcel}>ส่งออก Excel</button>
          <button className="btn btn-outline-danger" onClick={handleExportPDF}>ส่งออก PDF</button>
        </Col>
      </Row>

      <div className="mt-3">
        <Table bordered responsive>
          <thead className="table-light">
            <tr>
              <th rowSpan={3} className="text-center align-middle">ที่</th>
              <th rowSpan={3} className="text-center align-middle">เลขที่คำขอ</th>
              <th rowSpan={3} className="text-center align-middle">ชื่อผู้ขอรับเงินช่วยเหลือหรืออุดหนุน</th>
              {showDeptCol && (
                <th rowSpan={3} className="text-center align-middle">หน่วยงานที่อนุมัติคำขอ</th>
              )}
              <th colSpan={5} className="text-center align-middle">การรับรองมาตรฐานฝีมือแรงงานของผู้ประกอบอาชีพ</th>

              <th colSpan={2} className="text-center align-middle">ดำเนินการทดสอบให้แก่ลูกจ้าง</th>
              <th rowSpan={3} className="text-center align-middle">จำนวนเงินช่วยเหลือหรืออุดหนุน (บาท)</th>
              <th rowSpan={3} className="text-center align-middle">วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน</th>
            </tr>
            <tr>
              <th rowSpan={2} className="text-center">หนังสือรับรองเลขที่</th>
              <th rowSpan={2} className="text-center">ออกให้ ณ วันที่</th>
              <th className="text-center">สาขาอาชีพ</th>
              <th className="text-center">สาขา</th>
              <th className="text-center">ระดับ</th>
              <th rowSpan={2} className="text-center">ลูกจ้างในตำแหน่งงานที่เกี่ยวข้อง (คน)</th>
              <th rowSpan={2} className="text-center">จำนวนผู้เข้ารับการทดสอบ (คน)</th>
            </tr>
          </thead>

          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={showDeptCol ? 12 : 11} className="text-center">ยังไม่มีรายการ</td></tr>
            )}

            {Object.entries(groupBy(items, "SupportNo")).map(([sn, rows], gIdx) => {
              const span = rows.length;
              const uPayAmt = isUniform(rows, "PaymentAmount");
              const uPayDt = isUniform(rows, "PaymentDate");

              return rows.map((r, idx) => (
                <tr key={`${sn}-${idx}`}>
                  <td className="text-center">{idx === 0 ? gIdx + 1 : ""}</td>
                  {idx === 0 && <td className="text-center" rowSpan={span}>{r.SupportNo || "-"}</td>}
                  {idx === 0 && <td className="text-start" rowSpan={span}>{r.SupportName || "-"}</td>}
                  {showDeptCol && idx === 0 && <td className="text-start" rowSpan={span}>{r.DeptName || "-"}</td>}

                  <td className="text-start">{r.CertNo || "-"}</td>
                  <td className="text-center">{toThaiDateLabel(r.CertDate)}</td>
                  <td className="text-start">{r.LawOccupationName || "-"}</td>
                  <td className="text-start">{r.Branch || "-"}</td>
                  <td className="text-center">{r.Level || "-"}</td>
                  <td className="text-end">{r.EmployeeTotal}</td>
                  <td className="text-end">{r.PersonTotal}</td>

                  {uPayAmt
                    ? (idx === 0
                      ? <td className="text-end" rowSpan={span}>{fmtMoney(rows[0].PaymentAmount)}</td>
                      : null)
                    : <td className="text-end">{fmtMoney(r.PaymentAmount)}</td>}

                  {uPayDt
                    ? (idx === 0
                      ? <td className="text-center" rowSpan={span}>{toThaiDateLabel(rows[0].PaymentDate)}</td>
                      : null)
                    : <td className="text-center">{toThaiDateLabel(r.PaymentDate)}</td>}
                </tr>
              ));
            })}
          </tbody>

          {items.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={showDeptCol ? 11 : 10} className="text-end fw-bold">รวมจำนวนเงินช่วยเหลือ</td>
                <td className="text-end fw-bold">{fmtMoney(totalPayment)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {loading && (
        <div className="position-fixed bottom-0 end-0 p-3">
          <div className="badge bg-secondary">กำลังโหลด...</div>
        </div>
      )}
    </Container>
  );
}
